import {Component} from "@angular/core";
import {ActivatedRoute} from "@angular/router";
import {AdminService} from "../../services/admin.service";
import {SharedService} from "../../services/sharedService";
import {DocumentService} from "../../services/document.service";
import {ToastrService} from "ngx-toastr";
import {saveAs} from 'file-saver';
import {
    HelpCard, HELP_CATEGORIES, DEFAULT_HELP_CARDS, mergeHelpCards, searchHelpCards, textFor,
} from './help-cards';

const KEY = 'HOME_HELP_TEXT';
export const CARDS_KEY = 'HELP_CARDS';
// Document links are marked with a fragment href rather than a data-* attribute
// because Angular's sanitizer strips data-* but keeps fragment hrefs.
export const DOC_HREF_PREFIX = '#document-';

/**
 * The help page: one card per feature, with a search box over them.
 *
 * It used to render a single block of rich text an administrator had typed into
 * a WYSIWYG editor -- which meant explaining the whole application was one
 * person's writing job, and in practice the page said almost nothing. The cards
 * ship with the application (see help-cards.ts) and an administrator edits the
 * ones that need to say something different here.
 *
 * The old rich text is NOT discarded. Where a deployment has written something
 * into it, it still appears under the cards, with its document attachments
 * working as before: throwing away what an administrator wrote in order to
 * replace the page would be the worst kind of upgrade.
 */
@Component({
    selector: 'app-help',
    templateUrl: './help.component.html',
    styleUrls: ['./help.component.css'],
    standalone: false
})
export class HelpComponent {

    /** The legacy rich-text block, when a deployment has one. */
    helpContent: any = '';

    cards: HelpCard[] = DEFAULT_HELP_CARDS;
    visible: HelpCard[] = DEFAULT_HELP_CARDS;
    readonly categories = HELP_CATEGORIES;
    search = '';
    lang = 'en';

    constructor(private settings: AdminService,
                private shared: SharedService,
                private documentService: DocumentService,
                private toastr: ToastrService,
                private route: ActivatedRoute) {
        this.lang = this.shared.getLan() || 'en';
        this.shared.getLan$().subscribe(lan => {
            this.lang = lan || 'en';
            this.loadHelpText();
        });
        this.loadHelpText();

        /*
         * The side menu lists the cards, and each entry is a fragment link to
         * one. Two things have to happen that the router's own anchor scrolling
         * cannot do on its own:
         *
         *  - a search that is filtering the target card out has to be cleared,
         *    or the link lands on a card that is not rendered;
         *  - the scroll has to wait for the card to exist, since the list is
         *    re-rendered when the filter clears.
         */
        this.route.fragment.subscribe(fragment => {
            if (!fragment) return;
            if (this.search && !this.visible.some(card => card.id === fragment)) {
                this.clearSearch();
            }
            setTimeout(() => this.scrollTo(fragment), 0);
        });
    }

    private scrollTo(id: string) {
        const target = document.getElementById(id);
        if (!target) return;
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    loadHelpText() {
        this.settings.getAdminSettings().subscribe({
            next: (settings) => {
                const record = (settings && settings.length > 0) ? settings[0] : null;
                const raw = record?.[KEY]?.[this.shared.getLan()];
                this.helpContent = raw ? this.prepareHelpHtml(raw) : '';
                this.cards = mergeHelpCards(record?.[CARDS_KEY]);
                this.applySearch();
            },
            // The shipped cards are the point of the page, so a settings request
            // that does not come back leaves help readable rather than empty.
            error: () => {
                this.cards = DEFAULT_HELP_CARDS;
                this.applySearch();
            },
        });
    }

    applySearch() {
        this.visible = searchHelpCards(this.cards, this.search);
    }

    clearSearch() {
        this.search = '';
        this.applySearch();
    }

    /** The cards of one category that survived the search, in catalogue order. */
    cardsIn(category: string): HelpCard[] {
        return this.visible.filter(card => card.category === category);
    }

    text(value: any): string {
        return textFor(value, this.lang);
    }

    // This content is authored server-side and was previously rendered through
    // bypassSecurityTrustHtml, which disabled Angular's sanitizer completely and
    // made the help page a stored-XSS sink: anything saved into HOME_HELP_TEXT
    // executed for every user who opened it, and could read the access_token
    // from localStorage. It is now bound with plain [innerHTML], so Angular
    // sanitizes it and strips script elements and on* handlers.
    //
    // Angular's sanitizer also strips data-* attributes. Help text saved before
    // this change marks document links with data-document-id, so those are
    // rewritten to a fragment href, which survives sanitization. DOMParser
    // builds an inert document: it does not execute script and does not load
    // resources, and Angular still sanitizes the result afterwards.
    prepareHelpHtml(raw: string): string {
        try {
            const doc = new DOMParser().parseFromString(raw, 'text/html');
            doc.querySelectorAll('a[data-document-id]').forEach(anchor => {
                const id = (anchor.getAttribute('data-document-id') || '').replace(/[^a-zA-Z0-9\-_]/g, '');
                if (id) {
                    anchor.setAttribute('href', DOC_HREF_PREFIX + id);
                }
            });
            return doc.body.innerHTML;
        } catch {
            // Never fall back to the raw string: that would reintroduce the
            // unsanitized path this method exists to close.
            return '';
        }
    }

    onContentClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        const anchor = target.closest('a[href^="' + DOC_HREF_PREFIX + '"]') as HTMLAnchorElement;
        if (!anchor) return;

        event.preventDefault();
        const id = anchor.getAttribute('href').substring(DOC_HREF_PREFIX.length);
        const name = anchor.textContent || 'document';
        this.documentService.get$(id).subscribe(
            (blob: Blob) => saveAs(blob, name),
            () => this.toastr.error('Failed to download ' + name)
        );
    }
}
